/*
  Mobile menu V3.

  Its own element rather than Dawn's <header-drawer>: MenuDrawer traps focus
  inside an opened submenu, which is right for a slide-in panel and wrong for
  an inline accordion, and it is shared with the collection filter drawer.
  global.js stays untouched.

  The details ids deliberately avoid the "Details-" prefix that global.js
  attaches its own summary handlers to.
*/
class HeaderDrawerV3 extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('.mdv3-container');
    this.summary = this.details && this.details.querySelector('summary.mdv3__toggle');
    this.panel = this.querySelector('.mdv3');
    if (!this.details || !this.summary || !this.panel) return;

    this.header = document.querySelector('.section-header');
    this.closeTimer = null;

    this.summary.addEventListener('click', this.onToggleClick.bind(this));

    /*
      On document, not on this element: a mouse click blurs the summary (see
      onToggleClick), so a keyup bound here would never fire afterwards.
    */
    this.onDocumentKeyUp = this.onKeyUp.bind(this);
    document.addEventListener('keyup', this.onDocumentKeyUp);

    this.groups = Array.from(this.querySelectorAll('.mdv3__group'));
    this.groups.forEach((group) => {
      const summary = group.querySelector(':scope > summary');
      if (summary) summary.addEventListener('click', this.onGroupClick.bind(this, group));
    });

    this.onResize = this.setTopOffset.bind(this);
    this.setTopOffset();
  }

  /* The panel starts under the header, which is sticky and can change height. */
  setTopOffset() {
    if (!this.header) return;
    const bottom = Math.max(0, Math.round(this.header.getBoundingClientRect().bottom));
    document.documentElement.style.setProperty('--mdv3-top', `${bottom}px`);
  }

  onToggleClick(event) {
    event.preventDefault();
    this.details.hasAttribute('open') && this.details.classList.contains('menu-opening')
      ? this.close()
      : this.open();

    /*
      Chrome treats a mouse click on a <summary> as focus-visible, so .focus-inset
      draws its ring around the X for the whole time the menu is open — the design
      has no box there. Keyboard activation reports detail 0 and keeps its ring.
    */
    if (event.detail > 0) this.summary.blur();
  }

  open() {
    window.clearTimeout(this.closeTimer);
    this.setTopOffset();
    this.details.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', 'true');
    document.body.classList.add('overflow-hidden-tablet');
    window.addEventListener('resize', this.onResize);

    // Next frame, so the transition runs from the translated-out state.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.details.classList.add('menu-opening'));
    });
  }

  close() {
    window.clearTimeout(this.closeTimer);
    this.details.classList.remove('menu-opening');
    this.summary.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('overflow-hidden-tablet');
    window.removeEventListener('resize', this.onResize);

    this.closeTimer = window.setTimeout(() => {
      this.details.removeAttribute('open');
      this.groups.forEach((group) => this.collapse(group, false));
    }, 300);
  }

  disconnectedCallback() {
    document.removeEventListener('keyup', this.onDocumentKeyUp);
    window.removeEventListener('resize', this.onResize);
  }

  onKeyUp(event) {
    if (event.code !== 'Escape') return;
    if (!this.details.hasAttribute('open')) return;
    const target = event.target instanceof Element ? event.target : null;
    const openGroup = target && this.contains(target) ? target.closest('.mdv3__group[open]') : null;
    if (openGroup) {
      this.collapse(openGroup, true);
      const summary = openGroup.querySelector(':scope > summary');
      if (summary) summary.focus();
      return;
    }
    if (this.details.hasAttribute('open')) {
      this.close();
      this.summary.focus();
    }
  }

  /* Accordion. <details> alone cannot animate, so open/close is driven here. */
  onGroupClick(group, event) {
    event.preventDefault();
    group.hasAttribute('open') ? this.collapse(group, true) : this.expand(group);
    if (event.detail > 0) event.currentTarget.blur();
  }

  expand(group) {
    const panel = group.querySelector(':scope > .mdv3__panel');
    if (!panel) return;

    window.clearTimeout(group.mdv3Timer);
    group.setAttribute('open', '');
    const summary = group.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', 'true');

    // A closed <details> hides the panel outright, so give the transition an
    // explicit start value and flush it before growing to the content height.
    panel.style.maxHeight = '0px';
    void panel.offsetHeight;
    panel.style.maxHeight = `${panel.scrollHeight}px`;

    // Drop the cap once open so nested accordions are not clipped.
    group.mdv3Timer = window.setTimeout(() => {
      if (group.hasAttribute('open')) panel.style.maxHeight = 'none';
    }, 250);
  }

  collapse(group, animate) {
    const panel = group.querySelector(':scope > .mdv3__panel');
    if (!panel) return;

    window.clearTimeout(group.mdv3Timer);
    const summary = group.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', 'false');

    if (!animate) {
      group.removeAttribute('open');
      panel.style.maxHeight = '';
      return;
    }

    panel.style.maxHeight = `${panel.scrollHeight}px`;
    void panel.offsetHeight; // flush, so the transition has a start value
    panel.style.maxHeight = '0px';

    group.mdv3Timer = window.setTimeout(() => {
      group.removeAttribute('open');
      panel.style.maxHeight = '';
    }, 250);
  }
}

customElements.define('header-drawer-v3', HeaderDrawerV3);
